<script setup lang="ts">
import { countryOptions } from '~/data/countryOptions'
import { useAuthStore } from '~/stores/auth'

const authStore = useAuthStore()

const { t } = useI18n()
const { updateUser, setUser } = useUserService()

const props = defineProps<{
  initialHomebase?: string
}>()

const emit = defineEmits(['saved', 'close'])

const state = reactive<{
  homebase: string
  userHasInteracted: boolean
  loading: boolean
}>({
  homebase: props.initialHomebase || '',
  userHasInteracted: false,
  loading: false
})

watch(
  () => props.initialHomebase,
  (value) => {
    if (value && !state.userHasInteracted) {
      state.homebase = value
    }
  }
)

const saveSelection = async () => {
  if (!state.homebase) {
    return
  }

  if (!authStore.currentUser) {
    return
  }

  state.loading = true

  try {
    const updatedUser = await authStore.awaitAndSetLatestUserData(
      updateUser(authStore.currentUser.id, {
        homebase: state.homebase
      })
    )

    if (!updatedUser) {
      return
    }

    setUser(updatedUser)

    emit('saved')
  } catch (e) {
    console.error(e)
  } finally {
    state.loading = false
  }
}
</script>

<template>
  <Modal max-height="500px" data-testid="homebase-prompt-dialog" @close="emit('close')">
    <template #header>
      <span class="tw-font-medium">{{ t('homebase_dialog_title') }}</span>
    </template>

    <template #body>
      <div class="tw-flex tw-flex-col tw-gap-4 tw-py-4">
        <p class="tw-text-sm tw-leading-snug">
          {{ t('homebase_dialog_description') }}
        </p>

        <div class="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-p-2">
          <span class="tw-text-xl tw-leading-none">
            {{ state.homebase ? getFlagEmoji(state.homebase) : '&#127758;' }}
          </span>

          <InputNativeSelect
            v-model="state.homebase"
            :options="countryOptions"
            wrapper-classes="!tw-bg-transparent tw-w-full"
            input-classes="!tw-py-0 !tw-pl-0"
            @update:model-value="state.userHasInteracted = true"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="tw-w-full tw-px-4 tw-py-2">
        <InputAppButton
          :loading="state.loading"
          :disabled="!state.homebase"
          class="tw-w-full tw-rounded-xl tw-bg-black tw-py-2 tw-text-center tw-text-xl tw-text-white"
          @click="saveSelection"
        >
          {{ t('Save') }}
        </InputAppButton>
      </div>
    </template>
  </Modal>
</template>
